import { useState, useEffect, useCallback } from 'react';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { apiEndpoints } from '../utils/api';

interface Message {
  id: string;
  thread_id: string;
  role: string;
  content: Array<{ type: string; text?: string }>;
  sequence_no: number;
  timestamp: string;
  attachments?: string[];
  sub_agent_info?: Record<string, any>;
}

interface GetMessagesOptions {
  includeSubAgentMessages?: boolean;
  attempt?: number;
}

/**
 * Custom hook for fetching messages for a fix
 * @param organizationId - The ID of the organization
 * @param fixId - The ID of the fix
 * @returns Object containing messages, loading state, error, and fetch function
 */
const useFixMessages = (organizationId: string | undefined, fixId: string | undefined) => {
  const authenticatedApi = useAuthenticatedApi();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Clear messages when fix changes
  useEffect(() => {
    setMessages([]);
    setError(null);
  }, [organizationId, fixId]);

  /**
   * Get the ID from a message object
   */
  const getMessageId = (message: any): string | null => {
    return message?.id || message?._id || null;
  };

  /**
   * Fetches all messages for the fix
   * @param options - Query options
   * @param options.includeSubAgentMessages - If true, includes sub-agent messages inline
   */
  const getMessages = useCallback(async (options: GetMessagesOptions = {}): Promise<Message[] | null> => {
    if (!organizationId || !fixId) {
      setError(new Error('Organization ID and Fix ID are required'));
      return null;
    }

    const { includeSubAgentMessages = false, attempt } = options;

    setLoading(true);
    setError(null);

    try {
      const queryParams = new URLSearchParams();
      if (includeSubAgentMessages) {
        queryParams.append('include_sub_agent_messages', 'true');
      }
      if (attempt) {
        queryParams.append('attempt', String(attempt));
      }

      const baseEndpoint = apiEndpoints.fixes.messages(organizationId, fixId);
      const endpoint = queryParams.toString() ? `${baseEndpoint}?${queryParams.toString()}` : baseEndpoint;
      const data = await authenticatedApi.get<Message[]>(endpoint);

      // Transform data to ensure each message has an id property
      const normalizedData = data.map(message => ({
        ...message,
        id: getMessageId(message) || message.id,
      }));

      setMessages(normalizedData);
      setLoading(false);
      return normalizedData;
    } catch (err) {
      console.error('Error fetching fix messages:', err);
      setError(err as Error);
      setLoading(false);
      return null;
    }
  }, [authenticatedApi, organizationId, fixId]);

  return {
    messages,
    loading,
    error,
    getMessages,
    setMessages,
  };
};

export default useFixMessages;
